import React, { useState } from "react";
import { Grid, TextField, FormControl, Button, Typography } from "@mui/material";
import { AttachFile, CheckCircle } from "@mui/icons-material";

function PermitInfoTab({
	startingDate,
	setStartingDate,
	finishingDate,
	setFinishingDate,
	numberOfBuildings,
	setNumberOfBuildings,
	residenceCount,
	setResidenceCount,
	commercialCount,
	setCommercialCount,
	shedCount,
	setShedCount,
	otherCount,
	setOtherCount,
	modificationDetails,
	setModificationDetails,
	architecturalPlan,
	setArchitecturalPlan,
	structuralPlan,
	setStructuralPlan,
	sanitaryPlan,
	setSanitaryPlan,
	electricalPlan,
	setElectricalPlan,
	electroMechanicalPlan,
	setElectroMechanicalPlan,
	environmentalPlan,
	setEnvironmentalPlan,
	otherPlan,
	setOtherPlan,
	otherPlanDetails,
	setOtherPlanDetails,
}) {
	// State to track which files have been selected for icon display
	const [files, setFiles] = useState({
		architectural: null,
		structural: null,
		sanitary: null,
		electrical: null,
		electroMechanical: null,
		environmental: null,
		other: null
	});

	// FIXED: Correct handleFileChange function for PermitInfoTab
	const handleFileChange = (e, planType) => {
		const file = e.target.files[0];
		
		// Update the individual plan states (the props passed from parent)
		switch (planType) {
			case "architectural":
				setArchitecturalPlan(file);
				break;
			case "structural":
				setStructuralPlan(file);
				break;
			case "sanitary":
				setSanitaryPlan(file);
				break;
			case "electrical":
				setElectricalPlan(file);
				break;
			case "electroMechanical":
				setElectroMechanicalPlan(file);
				break;
			case "environmental":
				setEnvironmentalPlan(file);
				break;
			case "other":
				setOtherPlan(file);
				break;
			default:
				break;
		}

		// Update files state for icon display
		if (file) {
			setFiles(prev => ({
				...prev,
				[planType]: file
			}));
		}
	};

	// Calculate number of buildings based on building types
	const totalBuildings =
		(parseInt(residenceCount) || 0) + 
		(parseInt(commercialCount) || 0) + 
		(parseInt(shedCount) || 0) + 
		(parseInt(otherCount) || 0);

	// Validate starting date and finishing date
	const handleStartingDateChange = (e) => {
		try {
			const newStartingDate = e.target.value;
			setStartingDate(newStartingDate);
		} catch (error) {
			console.log(error)
		}
	};

	const handleFinishingDateChange = (e) => {
		try {
			const newFinishingDate = e.target.value;
			if (newFinishingDate && startingDate && newFinishingDate < startingDate) {
				// If finishing date is less than starting date, don't update
				return;
			}
			setFinishingDate(newFinishingDate);
		} catch (error) {
			console.log(error)
		}
	};

	return (
		<Grid container spacing={2} paddingLeft={6} paddingRight={6}>
			<Grid item xs={12} md={6}>
				<TextField
					label="Construction Starting Date"
					type="date"
					value={startingDate}
					onChange={handleStartingDateChange}
					fullWidth={true}
					InputLabelProps={{ shrink: true }}
					required
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					label="Construction Finishing Date"
					type="date"
					value={finishingDate}
					onChange={handleFinishingDateChange}
					fullWidth={true}
					InputLabelProps={{ shrink: true }}
					required
				/>
			</Grid>
			<Grid item xs={12} md={12}>
				<Grid container spacing={1}>
					<Grid item xs={12}>
						<Typography variant="subtitle1">Building Types</Typography>
					</Grid>
					
					<Grid item xs={12} md={2}>
						<TextField
							label="Residence"
							type="number"
							value={residenceCount}
							onChange={(e) => setResidenceCount(e.target.value)}
							fullWidth={true}
						/>
					</Grid>
					<Grid item xs={6} md={2}>
						<TextField
							label="Commercial"
							type="number"
							value={commercialCount}
							onChange={(e) => setCommercialCount(e.target.value)}
							fullWidth={true}
						/>
					</Grid>
					<Grid item xs={6} md={2}>
						<TextField
							label="Shed"
							type="number"
							value={shedCount}
							onChange={(e) => setShedCount(e.target.value)}
							fullWidth={true}
						/>
					</Grid>
					<Grid item xs={6} md={2}>
						<TextField
							label="Other"
							type="number"
							value={otherCount}
							onChange={(e) => setOtherCount(e.target.value)}
							fullWidth={true}
						/>
					</Grid>
					<Grid item xs={12} md={3}>
						<TextField
							label="Total No. of Buildings"
							type="number"
							value={totalBuildings} // Display calculated total
							onChange={(e) => setNumberOfBuildings(e.target.value)} // Still allow manual input
							fullWidth={true}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12} md={12}>
				<TextField
					label="Modification Details (if applicable)"
					value={modificationDetails}
					onChange={(e) => setModificationDetails(e.target.value)}
					fullWidth={true}
				/>
			</Grid>
			<Grid item xs={12}>
				<Typography variant="subtitle1">Plan attachments (PDF Files recommended)</Typography>
			</Grid>
			
			{/* File upload buttons with conditional icons */}
			<Grid item xs={12} md={4}>
				<FormControl fullWidth={true}>
			   <Button 
				variant="contained" 
				component="label" 
				startIcon={files.architectural ? <CheckCircle /> : <AttachFile />}
				sx={{ 
					backgroundColor: '#D0EBD0',
					'&:hover': {
					backgroundColor: '#C0DBC0', // Slightly darker shade for hover effect
					},
					color: '#2e7d32', // Dark green text color for better contrast
					textTransform: 'none',
					'& .MuiSvgIcon-root': {
					color: '#2e7d32' // Dark green icon color
					}
				}}
				>
				Architectural plan
				<input
					type="file"
					accept=".dwg,.pdf,.docx,.doc,.jpeg,.jpg,.png"
					onChange={(e) => handleFileChange(e, "architectural")}
					style={{ display: "none" }}
				/>
				</Button>
				</FormControl>
			</Grid>
			<Grid item xs={12} md={4}>
  <FormControl fullWidth={true}>
    <Button 
      variant="contained" 
      component="label" 
      startIcon={files.structural ? <CheckCircle /> : <AttachFile />}
      sx={{ 
        backgroundColor: files.structural ? '#4caf50' : '#D0EBD0',
        '&:hover': {
          backgroundColor: files.structural ? '#45a049' : '#C0DBC0',
        },
        color: files.structural ? '#ffffff' : '#2e7d32',
        textTransform: 'none'
      }}
    >
      Structural plan
      <input
        type="file"
        accept=".dwg,.pdf,.docx,.doc,.jpeg,.jpg,.png"
        onChange={(e) => handleFileChange(e, "structural")}
        style={{ display: "none" }}
      />
    </Button>
  </FormControl>
</Grid>

<Grid item xs={12} md={4}>
  <FormControl fullWidth={true}>
    <Button 
      variant="contained" 
      component="label" 
      startIcon={files.sanitary ? <CheckCircle /> : <AttachFile />}
      sx={{ 
        backgroundColor: files.sanitary ? '#4caf50' : '#D0EBD0',
        '&:hover': {
          backgroundColor: files.sanitary ? '#45a049' : '#C0DBC0',
        },
        color: files.sanitary ? '#ffffff' : '#2e7d32',
        textTransform: 'none'
      }}
    >
      Sanitary plan
      <input
        type="file"
        accept=".dwg,.pdf,.docx,.doc,.jpeg,.jpg,.png"
        onChange={(e) => handleFileChange(e, "sanitary")}
        style={{ display: "none" }}
      />
    </Button>
  </FormControl>
</Grid>

<Grid item xs={12} md={4}>
  <FormControl fullWidth={true}>
    <Button 
      variant="contained" 
      component="label" 
      startIcon={files.electrical ? <CheckCircle /> : <AttachFile />}
      sx={{ 
        backgroundColor: files.electrical ? '#4caf50' : '#D0EBD0',
        '&:hover': {
          backgroundColor: files.electrical ? '#45a049' : '#C0DBC0',
        },
        color: files.electrical ? '#ffffff' : '#2e7d32',
        textTransform: 'none'
      }}
    >
      Electrical Plan
      <input
        type="file"
        accept=".dwg,.pdf,.docx,.doc,.jpeg,.jpg,.png"
        onChange={(e) => handleFileChange(e, "electrical")}
        style={{ display: "none" }}
      />
    </Button>
  </FormControl>
</Grid>

<Grid item xs={12} md={4}>
  <FormControl fullWidth={true}>
    <Button 
      variant="contained" 
      component="label" 
      startIcon={files.electroMechanical ? <CheckCircle /> : <AttachFile />}
      sx={{ 
        backgroundColor: files.electroMechanical ? '#4caf50' : '#D0EBD0',
        '&:hover': {
          backgroundColor: files.electroMechanical ? '#45a049' : '#C0DBC0',
        },
        color: files.electroMechanical ? '#ffffff' : '#2e7d32',
        textTransform: 'none'
      }}
    >
      Electro-Mechanical plan
      <input
        type="file"
        accept=".dwg,.pdf,.docx,.doc,.jpeg,.jpg,.png"
        onChange={(e) => handleFileChange(e, "electroMechanical")}
        style={{ display: "none" }}
      />
    </Button>
  </FormControl>
</Grid>

<Grid item xs={12} md={4}>
  <FormControl fullWidth={true}>
    <Button 
      variant="contained" 
      component="label" 
      startIcon={files.environmental ? <CheckCircle /> : <AttachFile />}
      sx={{ 
        backgroundColor: files.environmental ? '#4caf50' : '#D0EBD0',
        '&:hover': {
          backgroundColor: files.environmental ? '#45a049' : '#C0DBC0',
        },
        color: files.environmental ? '#ffffff' : '#2e7d32',
        textTransform: 'none'
      }}
    >
      Environmental plan
      <input
        type="file"
        accept=".dwg,.pdf,.docx,.doc,.jpeg,.jpg,.png"
        onChange={(e) => handleFileChange(e, "environmental")}
        style={{ display: "none" }}
      />
    </Button>
  </FormControl>
</Grid>

<Grid item xs={12} md={8}>
  <TextField
    label="Other Plan Details (if applicable)"
    value={otherPlanDetails}
    onChange={(e) => setOtherPlanDetails(e.target.value)}
    fullWidth={true}
  />
</Grid>

<Grid item xs={12} md={4}>
  <FormControl fullWidth={true}>
    <Button 
      variant="contained" 
      component="label" 
      startIcon={files.other ? <CheckCircle /> : <AttachFile />}
      sx={{ 
        backgroundColor: files.other ? '#4caf50' : '#D0EBD0',
        '&:hover': {
          backgroundColor: files.other ? '#45a049' : '#C0DBC0',
        },
        color: files.other ? '#ffffff' : '#2e7d32',
        textTransform: 'none'
      }}
    >
      Other plan
      <input
        type="file"
        accept=".dwg,.pdf,.docx,.doc,.jpeg,.jpg,.png"
        onChange={(e) => handleFileChange(e, "other")}
        style={{ display: "none" }}
      />
    </Button>
  </FormControl>
</Grid>
		</Grid>
	);
}

export default PermitInfoTab;